import { SingleValue } from 'react-select'
import { IConversationCredit } from '../components/student/new-portal/mypage/credit/ConversationCreditHistoryItem'
import { s_credit } from '../modules/i18n/strings/auto/wording'

export interface IRemainingCredits {
  regular: {
    credit_count_20m: number
    credit_count_40m: number
  }
  makeup: {
    credit_count_20m: number
    credit_count_40m: number
  }
}

export enum CREDIT_CATEGORY {
  REGULAR = 'regular',
  MAKEUP = 'makeup',
  LIMITED = 'limited',
  URGENT = 'urgent',
  AITUTOR = 'ai_tutor',
}

export enum CREDIT_USE_TYPE {
  USED,
  UNUSED,
}

export interface IUnusedCredit {
  unused_credit_count: number
  credit_title: string
  exp_dates: number
  expired_at: string
}

export interface IUsedCredit {
  used_credit_index: number
  lesson_theme: string
  lesson_time: string
  student_noshow: boolean
  cancellation_time: number | null
  expired: boolean
}

export interface IRemainingCreditsCount {
  regular_20_credit_count: number
  regular_40_credit_count: number
  makeup_20_credit_count: number
  makeup_40_credit_count: number
  timelimit_20_credit_count: number
  timelimit_40_credit_count: number
  urgent_20_credit_count: number
  urgent_40_credit_count: number
}

export enum CREDITS_AVAILABILITY_ID {
  ALL = 'all',
  USABLE = 'usable',
  UNUSABLE = 'unusable',
}

export const CREDITS_AVAILABILITY_FILTER = {
  [CREDITS_AVAILABILITY_ID.ALL]: s_credit.option1_1,
  [CREDITS_AVAILABILITY_ID.USABLE]: s_credit.option1_2,
  [CREDITS_AVAILABILITY_ID.UNUSABLE]: s_credit.option1_3,
}

export enum CREDITS_CATEGORY_ID {
  ALL = 'all',
  REGULAR = 'regular',
  MAKEUP = 'makeup',
  TIME_LIMIT = 'timelimit',
  URGENT = 'urgent',
}

export const CREDITS_CATEGORY_FILTER = {
  [CREDITS_CATEGORY_ID.ALL]: s_credit.option2_1,
  [CREDITS_CATEGORY_ID.REGULAR]: s_credit.option2_2,
  [CREDITS_CATEGORY_ID.MAKEUP]: s_credit.option2_3,
  [CREDITS_CATEGORY_ID.TIME_LIMIT]: s_credit.option2_4,
  [CREDITS_CATEGORY_ID.URGENT]: s_credit.option2_5,
}

export enum CREDITS_LENGTH_ID {
  ALL = 'all',
  IS_20_MIN = 'is20min',
  IS_40_MIN = 'is40min',
}

export const CREDITS_LENGTH_FILTER = {
  [CREDITS_LENGTH_ID.ALL]: s_credit.option3_1,
  [CREDITS_LENGTH_ID.IS_20_MIN]: s_credit.option3_2,
  [CREDITS_LENGTH_ID.IS_40_MIN]: s_credit.option3_3,
}

export const DEFAULT_SELECTED_OPTIONS = [
  { value: CREDITS_AVAILABILITY_ID.USABLE },
  { value: CREDITS_AVAILABILITY_ID.UNUSABLE },
  { value: CREDITS_CATEGORY_ID.REGULAR },
  { value: CREDITS_CATEGORY_ID.MAKEUP },
  { value: CREDITS_CATEGORY_ID.TIME_LIMIT },
  { value: CREDITS_CATEGORY_ID.URGENT },
  { value: CREDITS_LENGTH_ID.IS_20_MIN },
  { value: CREDITS_LENGTH_ID.IS_40_MIN },
]

export enum CouponType {
  CouponBundle = 'CouponBundle',
  ConversationCreditBundle = 'ConversationCreditBundle',
}
export interface ICoupon {
  id: number
  name: string
  name_eng: string
  lesson_duration: number
  exp_dates: number[]
  remain_credit_counts: number
  created_at: string
  started: boolean
  converted: boolean
  allow_refund: boolean
  is_regular_credit: boolean
  is_long: boolean
  allow_extension: boolean
  active_receipt: boolean
  active_certificate: boolean
  is_free: boolean
  disabled: boolean
  refunded: boolean
  purchase_id: number
  b2b_hr_member: boolean
  is_weekly_lesson_package: boolean
  type: CouponType.CouponBundle | CouponType.ConversationCreditBundle
}
export interface ICouponBundles extends ICoupon {
  derived_coupons: ICoupon[] | IConversationCredit[]
}

export interface ICreditStatus {
  credit_name: string
  credit_name_eng: string
  remain_credit_counts: number
  credit_exp_dates: number
  credit_exp_dates_text: string
  refunded: boolean
  refunded_credit_counts: number
  expired: boolean
  expired_credit_counts: number
}

export interface IUsedCreditList {
  lesson_id: number[]
  lesson_theme: string[]
  lesson_theme_kr: string[]
  lesson_time: string[]
  cancel_time: number[]
  is_student_noshow: number[]
}

export interface ICouponBundlesDetails extends ICoupon {
  deprecated_credit_counts: number
  current_credits_status: ICreditStatus[]
  used_credits_history: IUsedCreditList
}

export interface ICouponRemainingCredits {
  id: number
  name: string
  name_en: string
  allow_extension: boolean
  remaining_exp_dates: number
  max_extend_dates: number
}

export interface ICouponExtendInfo {
  unit_price: number
  purchase_type: string
  purchase_created_at: string
  product_line_name: string
  remaining_credits: ICouponRemainingCredits[]
  usable_points: number
}

export interface IWeeklyLessonProgressWeeks {
  week_number: number
  period: string
  formatted_start_time: string
  formatted_end_time: string
  status: 'done' | 'failed' | 'recovered' | 'undetermined'
  stage: 'past' | 'current' | 'future'
  achieved: boolean
  upcoming_lesson_info?: {
    formatted_date: string
    formatted_period: string
  }
  achievement_detail:
    | 'lesson_completed'
    | 'tutor_noshow'
    | 'tutor_cancelled'
    | 'graced'
    | 'student_noshow'
    | 'student_cancelled'
    | 'deducted'
    | 'no_credit'
}
export interface IWeeklyLessonProgressSummary {
  total_count: number
  refunded_count: number
  admin_cancelled_count: number
  usable_count: number
  used_count: number
}
export interface IWeeklyLessonProgress {
  productLineName: string
  duration: number
  weeks: IWeeklyLessonProgressWeeks[]
  summary: IWeeklyLessonProgressSummary
}
export interface IWeeklyLessonPackageWarning {
  week_number: number
  remaining_days: number
  remaining_hours: number
  end_time: string
}

export interface ISelectedOption {
  value: number
  label: string
}

export interface ISelectedCredit extends ICouponRemainingCredits {
  options: ISelectedOption[]
  isSelected: boolean
  selectedOption: SingleValue<ISelectedOption>
  amount: number
  id: number
}

interface IDeductionGraceCredit {
  id: number
  coupon_bundle_id: number
  obtainable_type: string
  obtainable_id: number
  state: string
  used_at: string
  week_number: number
  created_at: string
  updated_at: string
}

export interface IDeductionGracedInfo {
  credits: IDeductionGraceCredit[]
}
